import React, { useState, useEffect } from 'react';
import { lessonsAPI, studentsAPI, coachesAPI, programsAPI, locationsAPI } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { Calendar } from '../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { format } from 'date-fns';
import { Plus, Search, Loader2, CalendarIcon, CheckCircle, Clock, X, Filter } from 'lucide-react';
import { toast } from 'sonner';

const Lessons = () => {
  const { canEdit } = useAuth();
  const [lessons, setLessons] = useState([]);
  const [students, setStudents] = useState([]);
  const [coaches, setCoaches] = useState([]);
  const [programs, setPrograms] = useState([]);
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterDate, setFilterDate] = useState(null);
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterCoach, setFilterCoach] = useState('all');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [formData, setFormData] = useState({
    student_id: '',
    program_id: '',
    coach_id: '',
    location_id: '',
    scheduled_date: '',
    lesson_number: 1,
  });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [lessonsRes, studentsRes, coachesRes, programsRes, locationsRes] = await Promise.all([
        lessonsAPI.getAll({}),
        studentsAPI.getAll({}),
        coachesAPI.getAll({}),
        programsAPI.getAll(),
        locationsAPI.getAll({}),
      ]);
      setLessons(lessonsRes.data);
      setStudents(studentsRes.data);
      setCoaches(coachesRes.data);
      setPrograms(programsRes.data);
      setLocations(locationsRes.data);
    } catch (error) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.student_id || !formData.coach_id || !formData.scheduled_date) {
      toast.error('Please fill in all required fields');
      return;
    }

    setSubmitting(true);
    try {
      await lessonsAPI.create(formData);
      toast.success('Lesson scheduled successfully');
      setDialogOpen(false);
      resetForm();
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to schedule lesson');
    } finally {
      setSubmitting(false);
    }
  };

  const handleCompleteLesson = async (lessonId) => {
    try {
      await lessonsAPI.complete(lessonId);
      toast.success('Lesson marked as complete');
      loadData();
    } catch (error) {
      toast.error('Failed to complete lesson');
    }
  };

  const resetForm = () => {
    setFormData({
      student_id: '',
      program_id: '',
      coach_id: '',
      location_id: '',
      scheduled_date: '',
      lesson_number: 1,
    });
  };

  const handleStudentSelect = (studentId) => {
    const student = students.find(s => s.id === studentId);
    if (student) {
      setFormData({
        ...formData,
        student_id: studentId,
        program_id: student.program_id,
        location_id: student.location_id,
      });
    }
  };

  const filteredLessons = lessons.filter((lesson) => {
    const matchesDate = !filterDate || lesson.scheduled_date === format(filterDate, 'yyyy-MM-dd');
    const matchesStatus = filterStatus === 'all' || lesson.status === filterStatus;
    const matchesCoach = filterCoach === 'all' || lesson.coach_id === filterCoach;
    return matchesDate && matchesStatus && matchesCoach;
  });

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed': return 'bg-emerald-100 text-emerald-700';
      case 'scheduled': return 'bg-amber-100 text-amber-700';
      case 'cancelled': return 'bg-red-100 text-red-700';
      default: return 'bg-slate-100 text-slate-700';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-heading font-bold text-slate-900">Lessons</h1>
          <p className="text-slate-500">Schedule and track lesson progress</p>
        </div>
        {canEdit() && (
          <Button onClick={() => { resetForm(); setDialogOpen(true); }} className="btn-primary" data-testid="add-lesson-btn">
            <Plus className="w-4 h-4 mr-2" />
            Schedule Lesson
          </Button>
        )}
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-col sm:flex-row gap-4">
            <Popover>
              <PopoverTrigger asChild>
                <Button variant="outline" className="justify-start text-left font-normal w-full sm:w-48" data-testid="filter-date">
                  <CalendarIcon className="mr-2 h-4 w-4" />
                  {filterDate ? format(filterDate, 'PPP') : 'Pick a date'}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0" align="start">
                <Calendar
                  mode="single"
                  selected={filterDate}
                  onSelect={setFilterDate}
                  initialFocus
                />
              </PopoverContent>
            </Popover>
            {filterDate && (
              <Button variant="ghost" size="icon" onClick={() => setFilterDate(null)}>
                <X className="w-4 h-4" />
              </Button>
            )}
            <Select value={filterStatus} onValueChange={setFilterStatus}>
              <SelectTrigger className="w-full sm:w-40" data-testid="filter-lesson-status">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="scheduled">Scheduled</SelectItem>
                <SelectItem value="completed">Completed</SelectItem>
                <SelectItem value="cancelled">Cancelled</SelectItem>
              </SelectContent>
            </Select>
            <Select value={filterCoach} onValueChange={setFilterCoach}>
              <SelectTrigger className="w-full sm:w-48" data-testid="filter-lesson-coach">
                <SelectValue placeholder="Coach" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Coaches</SelectItem>
                {coaches.map((coach) => (
                  <SelectItem key={coach.id} value={coach.id}>
                    {coach.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Lessons Table */}
      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Lesson</TableHead>
                <TableHead>Student</TableHead>
                <TableHead className="hidden md:table-cell">Coach</TableHead>
                <TableHead className="hidden lg:table-cell">Location</TableHead>
                <TableHead>Date</TableHead>
                <TableHead>Status</TableHead>
                {canEdit() && <TableHead className="text-right">Action</TableHead>}
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredLessons.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={canEdit() ? 7 : 6} className="text-center py-12">
                    <CalendarIcon className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                    <p className="text-slate-400">No lessons found</p>
                  </TableCell>
                </TableRow>
              ) : (
                filteredLessons.map((lesson) => (
                  <TableRow key={lesson.id} data-testid={`lesson-row-${lesson.id}`}>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                          lesson.status === 'completed' ? 'bg-emerald-100 text-emerald-600' : 'bg-amber-100 text-amber-600'
                        }`}>
                          {lesson.status === 'completed' ? (
                            <CheckCircle className="w-4 h-4" />
                          ) : (
                            <Clock className="w-4 h-4" />
                          )}
                        </div>
                        <span className="font-medium">#{lesson.lesson_number}</span>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div>
                        <p className="font-medium text-slate-900">{lesson.student_name}</p>
                        <p className="text-xs text-slate-500">{lesson.program_name}</p>
                      </div>
                    </TableCell>
                    <TableCell className="hidden md:table-cell">
                      {lesson.coach_name || '-'}
                    </TableCell>
                    <TableCell className="hidden lg:table-cell">
                      {lesson.location_name || '-'}
                    </TableCell>
                    <TableCell>{lesson.scheduled_date}</TableCell>
                    <TableCell>
                      <Badge className={getStatusColor(lesson.status)}>
                        {lesson.status}
                      </Badge>
                    </TableCell>
                    {canEdit() && (
                      <TableCell className="text-right">
                        {lesson.status === 'scheduled' && (
                          <Button
                            size="sm"
                            onClick={() => handleCompleteLesson(lesson.id)}
                            data-testid={`complete-lesson-btn-${lesson.id}`}
                          >
                            <CheckCircle className="w-4 h-4 mr-1" />
                            Complete
                          </Button>
                        )}
                      </TableCell>
                    )}
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Schedule Lesson Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Schedule New Lesson</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label>Student *</Label>
              <Select
                value={formData.student_id}
                onValueChange={handleStudentSelect}
              >
                <SelectTrigger data-testid="lesson-student-select">
                  <SelectValue placeholder="Select student" />
                </SelectTrigger>
                <SelectContent>
                  {students.map((student) => (
                    <SelectItem key={student.id} value={student.id}>
                      {student.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Coach *</Label>
              <Select
                value={formData.coach_id}
                onValueChange={(value) => setFormData({ ...formData, coach_id: value })}
              >
                <SelectTrigger data-testid="lesson-coach-select">
                  <SelectValue placeholder="Select coach" />
                </SelectTrigger>
                <SelectContent>
                  {coaches.map((coach) => (
                    <SelectItem key={coach.id} value={coach.id}>
                      {coach.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Location</Label>
              <Select
                value={formData.location_id}
                onValueChange={(value) => setFormData({ ...formData, location_id: value })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select location" />
                </SelectTrigger>
                <SelectContent>
                  {locations.map((location) => (
                    <SelectItem key={location.id} value={location.id}>
                      {location.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Date *</Label>
                <Input
                  type="date"
                  value={formData.scheduled_date}
                  onChange={(e) => setFormData({ ...formData, scheduled_date: e.target.value })}
                  data-testid="lesson-date-input"
                />
              </div>
              <div className="space-y-2">
                <Label>Lesson Number</Label>
                <Input
                  type="number"
                  value={formData.lesson_number}
                  onChange={(e) => setFormData({ ...formData, lesson_number: parseInt(e.target.value) })}
                  min={1}
                />
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={submitting} data-testid="submit-lesson-btn">
                {submitting && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                Schedule
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Lessons;
